import {Grid, Box, Stack, useTheme, useMediaQuery} from "@mui/material";
import TotalizerAlarms from "../TotalizerAlarms/TotalizerAlarms";
import AlarmPage from "../Alarms/AlarmPage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ADataGridFilter, AMultiSelectFilter, AMultiSelectOption, AMultiSelectOptionId, ASectionTitle } from "@atiautomacao/ati-ui-library";
import { LoadingButton } from "@mui/lab";
import SideBar from "../Alarms/Components/sideBarFilterAlarms";
import {ChipFilterAlarms} from "../Alarms/Components/ChipFilterAlarms";
import {Storage} from "../../../Utils/StorageUtils";
import {
    ALARM_ADVANCED_FILTERS_KEY,
    getOverviewAlarmCategoryFromFilters,
} from "../Alarms/Components/ChipFilterAlarms/types";
import { useAppDispatch, useAppSelector } from "../../../Config/Hooks";
import { RootState } from "../../../Config/Store";
import { usePersistedSelection } from "../../../Shared/Hooks/usePersistedSelection";
import { POWER_STATION_IDS_FILTER_STORAGE_KEY } from "../../../Config/Constants";
import { getTotalizers } from "../../../Shared/Components/AlarmTotalizers/AlarmTotalizersReducer";

export type TotalizerType =  "alarms_sound" | "not_acknowledged" | "power_station_no_communication"  | "equipment_no_communication" | "equipments_alarmed" | "active" | "active_critical" | "none";

const ALARM_FILTERS_COUNT_KEY = 'alarm_filters_count';
const TOTALIZER_ALARM_FILTER_KEY = 'totalizer_alarm_filter';

const getInitialAdvancedFilters = (): ADataGridFilter[] => {
    const stored = Storage.local.get(ALARM_ADVANCED_FILTERS_KEY, null);
    if (stored && Array.isArray(stored)) {
        const storedFilters = stored as ADataGridFilter[];
        if (getOverviewAlarmCategoryFromFilters(storedFilters)) {
            localStorage.setItem(TOTALIZER_ALARM_FILTER_KEY, 'none');
            return storedFilters.filter((filter) => filter.name !== 'totalizerType');
        }
        return storedFilters;
    }
    const storedTotalizer = localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none";
    if (storedTotalizer !== "none") {
        return [{ name: 'totalizerType', condition: 'equals', value: storedTotalizer, sort: 'none' }];
    }
    return [];
};

const getInitialFiltersCount = (): number => {
    const stored = Number(Storage.local.get(ALARM_FILTERS_COUNT_KEY, 0));
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
};

export default function DashboardOverviewAlarmsPage() {
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const dispatch = useAppDispatch();

    const [sideBarOpen, setSideBarOpen] = useState(false);
    const [totalizerFilter, setTotalizerFilter] = useState<TotalizerType>(() => {
        const initialFilters = getInitialAdvancedFilters();
        if (getOverviewAlarmCategoryFromFilters(initialFilters)) {
            return 'none';
        }
        return localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none";
    });
    const [advancedFilters, setAdvancedFilters] = useState<ADataGridFilter[]>(getInitialAdvancedFilters);
    const [isClearFilters, setIsClearFilters] = useState<boolean>(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState<number>(getInitialFiltersCount);

    const entitiesDashboard = useAppSelector((state: RootState) => state.dashboardNavigationMenu.entities);

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...entitiesDashboard]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map(e => ({ id: e.id, label: e.name })),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map(o => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) return;
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const powerStationFilter = useMemo<ADataGridFilter | null>(() => {
        if (selectedPowerStationIds.length === 0) return null;
        return {
            name: 'powerStationId',
            condition: 'equals',
            value: selectedPowerStationIds.join('~'),
            sort: 'none'
        };
    }, [selectedPowerStationIds]);

    const gridFilters = useMemo<ADataGridFilter[]>(
        () => powerStationFilter ? [...advancedFilters, powerStationFilter] : advancedFilters,
        [advancedFilters, powerStationFilter]
    );

    useEffect(() => {
        dispatch(getTotalizers(selectedPowerStationIds));
    }, [selectedPowerStationIds]);

    const mergeFiltersWithTotalizer = (sidebarFilters: ADataGridFilter[], totalizer: TotalizerType): ADataGridFilter[] => {
        const baseFilters = sidebarFilters.filter(f => f.name !== 'totalizerType');

        if (totalizer !== 'none') {
            baseFilters.push({
                name: 'totalizerType',
                condition: 'equals',
                value: totalizer,
                sort: 'none'
            });
        }

        return baseFilters;
    };

    const handleTotalizerChange = (totalizer: TotalizerType) => {
        localStorage.setItem(TOTALIZER_ALARM_FILTER_KEY, totalizer);
        setTotalizerFilter(totalizer);
        setAdvancedFilters(prev => {
            const merged = mergeFiltersWithTotalizer(prev, totalizer);
            Storage.local.set(ALARM_ADVANCED_FILTERS_KEY, merged);
            return merged;
        });
        setIsClearFilters(false);
    };

    const handleApplySideBarFilters = (sidebarFilters: ADataGridFilter[], filtersCount: number) => {
        const merged = mergeFiltersWithTotalizer(sidebarFilters, totalizerFilter);
        setAdvancedFilters(merged);
        Storage.local.set(ALARM_ADVANCED_FILTERS_KEY, merged);
        setSideBarOpen(false);
        setAppliedFiltersCount(filtersCount);
        Storage.local.set(ALARM_FILTERS_COUNT_KEY, filtersCount);
        setIsClearFilters(false);
    };

    const handleClearFilters = (cleared: boolean) => {
        if (cleared) {
            setAdvancedFilters([]);
            Storage.local.remove(ALARM_ADVANCED_FILTERS_KEY);
            setAppliedFiltersCount(0);
            Storage.local.remove(ALARM_FILTERS_COUNT_KEY);
            setIsClearFilters(true);
            Storage.local.remove(TOTALIZER_ALARM_FILTER_KEY);
        }
    };
        
    return (
            <Grid container paddingLeft={'24px'} paddingRight={'24px'} paddingBottom={'24px'}>
                <Grid item xs={12} paddingTop={0}>
                    <ASectionTitle
                        titleLabel={"Alarmes"}
                        styleContainer={{padding: '0px'}}
                        styleContainerTitle={{padding: '0px'}}
                        contentChildrenStyle={{display: 'flex'}}
                        contentChildren={
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <AMultiSelectFilter
                                    options={powerStationOptions}
                                    selectedIds={selectedPowerStationIds}
                                    onApply={handleApplyPowerStationFilter}
                                    triggerLabel="Usinas"
                                    searchPlaceholder="Buscar usina..."
                                />
                                <Box sx={{display: isXl ? 'none' : 'flex', height: '37px'}}>
                                    <LoadingButton
                                        size="small"
                                        sx={{lineHeight: 1}}
                                        loading={false}
                                        startIcon={
                                            <Box
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: '#fff',
                                                    fontSize: '0.75rem !important',
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {appliedFiltersCount}
                                            </Box>
                                        }
                                        variant="contained"
                                        onClick={() => setSideBarOpen(true)}
                                    >
                                        Filtros Avançados
                                    </LoadingButton>
                                </Box>
                            </Stack>
                        }
                    />
                </Grid>
                <Grid item xs={12} paddingTop={'16px'}>
                    <TotalizerAlarms onClickTotalizer={handleTotalizerChange} clearFilters={isClearFilters} 
                     />
                </Grid>
                <Grid item xs={12} paddingTop={'4px'} sx={{display: isXl ? 'block' : 'none'}}>
                    <ChipFilterAlarms onApplyFilters={handleApplySideBarFilters} hasClearFilters={isClearFilters} />
                </Grid>
                <Grid item xs={12} paddingTop={'24px'}>
                    <AlarmPage advancedFilters={gridFilters}
                    hasClearFilters={handleClearFilters} />
                </Grid>
                <SideBar
                    open={sideBarOpen}
                    onClose={() => setSideBarOpen(false)}
                    onApplyFilters={handleApplySideBarFilters}
                    hasClearFilters={isClearFilters}
                />
            </Grid>
    )
}
